import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import axiosInstance from '../api';
import './AccessReviewPage.css';

const RISK_ORDER = { OVERPRIVILEGED: 0, REVIEW_NEEDED: 1, LOW: 2 };
const RISK_CLASS = { OVERPRIVILEGED: 'risk-over', REVIEW_NEEDED: 'risk-review', LOW: 'risk-low' };
const RISK_LABEL = {
  OVERPRIVILEGED: `🔴 ${i18n.t('AccessReviewPage.riskOverprivileged')}`,
  REVIEW_NEEDED:  `🟡 ${i18n.t('AccessReviewPage.riskReviewNeeded')}`,
  LOW:            `🟢 ${i18n.t('AccessReviewPage.riskLow')}`,
};
const SYS_CLASS  = { AZURE_AD: 'sys-azure', GITHUB: 'sys-github', AWS_IAM: 'sys-aws' };
const SYS_LABEL  = { AZURE_AD: 'Azure AD', GITHUB: 'GitHub', AWS_IAM: 'AWS IAM' };

function RiskBadge({ level }) {
  return (
    <span className={`risk-badge ${RISK_CLASS[level] || 'risk-low'}`}>
      {RISK_LABEL[level] || level}
    </span>
  );
}

function SysChip({ system }) {
  return (
    <span className={`sys-chip ${SYS_CLASS[system] || ''}`}>
      <i className={`fas ${system === 'GITHUB' ? 'fa-code-branch' : system === 'AWS_IAM' ? 'fa-cloud' : 'fa-id-card'}`} />
      {SYS_LABEL[system] || system}
    </span>
  );
}

function ConfirmModal({ cycleLabel, onConfirm, onCancel, loading }) {
  const { t } = useTranslation();
  return (
    <div className="ar-modal-overlay">
      <div className="ar-modal">
        <div className="ar-modal-icon ar-modal-icon-warn">
          <i className="fas fa-lock" />
        </div>
        <h3 className="ar-modal-title">{t('AccessReviewPage.confirmTitle')}</h3>
        <p className="ar-modal-body">
          {t('AccessReviewPage.confirmBodyBefore')} <strong>{cycleLabel}</strong>{t('AccessReviewPage.confirmBodyAfter')}
        </p>
        <div className="ar-modal-actions">
          <button className="ar-modal-cancel" onClick={onCancel} disabled={loading}>{t('AccessReviewPage.cancel')}</button>
          <button className="ar-modal-confirm" onClick={onConfirm} disabled={loading}>
            {loading ? <><i className="fas fa-spinner fa-spin" /> {t('AccessReviewPage.finalizing')}</> : t('AccessReviewPage.yesFinalize')}
          </button>
        </div>
      </div>
    </div>
  );
}

function ReportBanner({ summary, onDownload, onDismiss }) {
  const { t } = useTranslation();
  return (
    <div className="ar-report-banner">
      <div className="ar-report-banner-icon">
        <i className="fas fa-check-circle" />
      </div>
      <div className="ar-report-banner-body">
        <div className="ar-report-banner-title">{t('AccessReviewPage.reportBannerTitle')}</div>
        <div className="ar-report-banner-stats">
          <span className="ar-rpt-stat">
            <i className="fas fa-users" /> {t('AccessReviewPage.statReviewed', { count: summary.totalReviewed })}
          </span>
          <span className="ar-rpt-stat ar-rpt-approved">
            <i className="fas fa-check" /> {t('AccessReviewPage.statApproved', { count: summary.approved })}
          </span>
          <span className="ar-rpt-stat ar-rpt-revoked">
            <i className="fas fa-times" /> {t('AccessReviewPage.statRevoked', { count: summary.revoked })}
          </span>
          <span className="ar-rpt-stat ar-rpt-pending">
            <i className="fas fa-clock" /> {t('AccessReviewPage.statPendingEscalated', { count: summary.totalReviewed - summary.approved - summary.revoked })}
          </span>
        </div>
        <div className="ar-report-banner-sub">{summary.reportSummary}</div>
      </div>
      <div className="ar-report-banner-actions">
        <button className="ar-download-btn" onClick={onDownload}>
          <i className="fas fa-download" /> {t('AccessReviewPage.downloadCsvReport')}
        </button>
        <button className="ar-dismiss-btn" onClick={onDismiss}>
          <i className="fas fa-times" />
        </button>
      </div>
    </div>
  );
}

function sortByRisk(items) {
  return [...items].sort((a, b) => (RISK_ORDER[a.riskLevel] ?? 9) - (RISK_ORDER[b.riskLevel] ?? 9));
}

function downloadCSV(cycleLabel, items) {
  const header = [
    i18n.t('AccessReviewPage.csvEmployee'),
    i18n.t('AccessReviewPage.csvEmail'),
    i18n.t('AccessReviewPage.csvSystem'),
    i18n.t('AccessReviewPage.csvRole'),
    i18n.t('AccessReviewPage.csvRiskLevel'),
    i18n.t('AccessReviewPage.csvDecision'),
    i18n.t('AccessReviewPage.csvDecidedBy'),
    i18n.t('AccessReviewPage.csvDecidedAt'),
  ];
  const rows = items.map(i => [
    i.displayName || '',
    i.email || '',
    SYS_LABEL[i.system] || i.system || '',
    i.role || '',
    i.riskLevel || '',
    i.decision || '',
    i.decidedBy || '',
    i.decidedAt ? new Date(i.decidedAt).toLocaleString() : '',
  ]);
  const csv = [header, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `${(cycleLabel || 'access-review').replace(/[^a-z0-9]/gi, '-').toLowerCase()}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function AccessReviewPage() {
  const { t } = useTranslation();
  const [reviews,      setReviews]      = useState([]);
  const [selected,     setSelected]     = useState(null);
  const [items,        setItems]        = useState([]);
  const [loadingRev,   setLoadingRev]   = useState(true);
  const [loadingItm,   setLoadingItm]   = useState(false);
  const [starting,     setStarting]     = useState(false);
  const [finalizing,   setFinalizing]   = useState(false);
  const [showConfirm,  setShowConfirm]  = useState(false);
  const [reportSummary, setReportSummary] = useState(null);
  const [errorMsg,     setErrorMsg]     = useState(null);
  const [bulkApproving, setBulkApproving] = useState(false);

  const loadReviews = useCallback(() => {
    setLoadingRev(true);
    axiosInstance.get('/access-reviews')
      .then(r => setReviews(r.data))
      .catch(() => {})
      .finally(() => setLoadingRev(false));
  }, []);

  useEffect(() => { loadReviews(); }, [loadReviews]);

  const selectReview = (rev) => {
    setSelected(rev);
    setReportSummary(null);
    setErrorMsg(null);
    setLoadingItm(true);
    axiosInstance.get(`/access-reviews/${rev.id}/items`)
      .then(r => setItems(sortByRisk(r.data)))
      .catch(() => setItems([]))
      .finally(() => setLoadingItm(false));
  };

  const startReview = () => {
    setStarting(true);
    setErrorMsg(null);
    axiosInstance.post('/access-reviews/start')
      .then(r => {
        loadReviews();
        selectReview(r.data);
      })
      .catch(() => setErrorMsg(t('AccessReviewPage.errorStart')))
      .finally(() => setStarting(false));
  };

  const setDecision = (itemId, decision) => {
    axiosInstance.put(`/access-reviews/items/${itemId}/decision`, { decision })
      .then(r => setItems(prev => prev.map(i => i.id === itemId ? r.data : i)))
      .catch(() => {});
  };

  const bulkApproveLow = () => {
    const targets = items.filter(i => i.decision === 'PENDING' && i.riskLevel === 'LOW');
    if (targets.length === 0) return;
    setBulkApproving(true);
    Promise.allSettled(
      targets.map(i => axiosInstance.put(`/access-reviews/items/${i.id}/decision`, { decision: 'APPROVED' }))
    ).then(results => {
      setItems(prev => prev.map(item => {
        const idx = targets.findIndex(t => t.id === item.id);
        if (idx === -1) return item;
        const res = results[idx];
        return res.status === 'fulfilled' ? res.value.data : item;
      }));
      const failed = results.filter(r => r.status === 'rejected').length;
      if (failed > 0) setErrorMsg(t('AccessReviewPage.errorBulkApprovePartial', { count: failed }));
    }).finally(() => setBulkApproving(false));
  };

  const doFinalize = () => {
    if (!selected) return;
    setFinalizing(true);
    axiosInstance.post(`/access-reviews/${selected.id}/finalize`)
      .then(r => {
        setReportSummary(r.data);
        loadReviews();
        setSelected(s => ({ ...s, status: 'FINALIZED' }));
        // Reload items so decision buttons disappear
        axiosInstance.get(`/access-reviews/${selected.id}/items`)
          .then(res => setItems(res.data)).catch(() => {});
      })
      .catch(() => setErrorMsg(t('AccessReviewPage.errorFinalize')))
      .finally(() => { setFinalizing(false); setShowConfirm(false); });
  };

  const hasOpenReview = reviews.some(r => r.status === 'OPEN');

  return (
    <div className="ar-page">
      {showConfirm && (
        <ConfirmModal
          cycleLabel={selected?.cycleLabel}
          onConfirm={doFinalize}
          onCancel={() => setShowConfirm(false)}
          loading={finalizing}
        />
      )}

      <div className="ar-header">
        <div>
          <h2><i className="fas fa-user-check" style={{ marginRight: 10, color: '#1d4ed8' }} />
            {t('AccessReviewPage.pageTitle')}
          </h2>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
            {t('AccessReviewPage.pageSubtitle')}
          </div>
        </div>
        <button className="ar-start-btn" onClick={startReview}
                disabled={starting || hasOpenReview}>
          <i className="fas fa-play" />
          {starting ? t('AccessReviewPage.starting') : hasOpenReview ? t('AccessReviewPage.reviewInProgress') : t('AccessReviewPage.startQuarterlyReview')}
        </button>
      </div>

      {errorMsg && (
        <div className="ar-error-banner">
          <i className="fas fa-exclamation-triangle" />
          {errorMsg}
          <button className="ar-error-close" onClick={() => setErrorMsg(null)}>
            <i className="fas fa-times" />
          </button>
        </div>
      )}

      {reportSummary && (
        <ReportBanner
          summary={reportSummary}
          onDownload={() => downloadCSV(selected?.cycleLabel, items)}
          onDismiss={() => setReportSummary(null)}
        />
      )}

      {loadingRev ? (
        <div className="ar-loading"><i className="fas fa-spinner fa-spin" /> {t('AccessReviewPage.loadingReviews')}</div>
      ) : reviews.length === 0 ? (
        <div className="ar-empty">
          <i className="fas fa-clipboard-list" />
          <div style={{ fontWeight: 600, fontSize: 16, color: '#334155' }}>{t('AccessReviewPage.emptyTitle')}</div>
          <div style={{ fontSize: 13, marginTop: 4 }}>{t('AccessReviewPage.emptyBody')}</div>
        </div>
      ) : (
        <div className="ar-reviews">
          {reviews.map(rev => (
            <div key={rev.id}
                 className={`ar-review-card ${selected?.id === rev.id ? 'active' : ''}`}
                 onClick={() => selectReview(rev)}>
              <div className="ar-review-top">
                <div>
                  <div className="ar-review-label">{rev.cycleLabel || t('AccessReviewPage.defaultCycleLabel')}</div>
                  <div className="ar-review-meta">
                    {t('AccessReviewPage.startedOn', { date: rev.startedAt ? new Date(rev.startedAt).toLocaleDateString() : '—' })}
                    {rev.reviewerUsername && ` · ${t('AccessReviewPage.byReviewer', { reviewer: rev.reviewerUsername })}`}
                    {rev.finalizedAt && ` · ${t('AccessReviewPage.finalizedOn', { date: new Date(rev.finalizedAt).toLocaleDateString() })}`}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                  <div className="ar-review-stats">
                    <div className="ar-stat ar-stat-pending">
                      <span className="ar-stat-val">{rev.pending ?? '—'}</span>
                      <span className="ar-stat-lbl">{t('AccessReviewPage.statLabelPending')}</span>
                    </div>
                    <div className="ar-stat ar-stat-approved">
                      <span className="ar-stat-val">{rev.approved ?? '—'}</span>
                      <span className="ar-stat-lbl">{t('AccessReviewPage.statLabelApproved')}</span>
                    </div>
                    <div className="ar-stat ar-stat-revoked">
                      <span className="ar-stat-val">{rev.revoked ?? '—'}</span>
                      <span className="ar-stat-lbl">{t('AccessReviewPage.statLabelRevoked')}</span>
                    </div>
                  </div>
                  <span className={`ar-badge ar-badge-${rev.status?.toLowerCase()}`}>
                    {rev.status === 'OPEN' ? `🔓 ${t('AccessReviewPage.statusOpen')}` : `✅ ${t('AccessReviewPage.statusFinalized')}`}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {selected && (
        <div className="ar-items-section">
          <div className="ar-items-header">
            <h3 className="ar-items-title">
              {t('AccessReviewPage.reviewItemsTitle', { label: selected.cycleLabel })}
            </h3>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              {items.length > 0 && selected.status === 'FINALIZED' && (
                <button className="ar-download-outline-btn"
                        onClick={() => downloadCSV(selected.cycleLabel, items)}>
                  <i className="fas fa-download" /> {t('AccessReviewPage.downloadCsv')}
                </button>
              )}
              {selected.status === 'OPEN' && items.some(i => i.decision === 'PENDING' && i.riskLevel === 'LOW') && (
                <button className="ar-bulk-approve-btn"
                        onClick={bulkApproveLow}
                        disabled={bulkApproving}>
                  <i className={`fas ${bulkApproving ? 'fa-spinner fa-spin' : 'fa-check-double'}`} style={{ marginRight: 6 }} />
                  {bulkApproving
                    ? t('AccessReviewPage.bulkApproving')
                    : t('AccessReviewPage.bulkApproveLow', {
                        count: items.filter(i => i.decision === 'PENDING' && i.riskLevel === 'LOW').length,
                      })}
                </button>
              )}
              {selected.status === 'OPEN' && (
                <button className="ar-finalize-btn"
                        onClick={() => setShowConfirm(true)}
                        disabled={finalizing}>
                  <i className="fas fa-check-double" style={{ marginRight: 6 }} />
                  {t('AccessReviewPage.finalizeExport')}
                </button>
              )}
            </div>
          </div>

          {loadingItm ? (
            <div className="ar-loading"><i className="fas fa-spinner fa-spin" /> {t('AccessReviewPage.loading')}</div>
          ) : items.length === 0 ? (
            <div className="ar-empty"><i className="fas fa-inbox" /> {t('AccessReviewPage.noItems')}</div>
          ) : (
            <table className="ar-table">
              <thead>
                <tr>
                  <th>{t('AccessReviewPage.thEmployee')}</th>
                  <th>{t('AccessReviewPage.thSystem')}</th>
                  <th>{t('AccessReviewPage.thRolePermission')}</th>
                  <th>{t('AccessReviewPage.thRisk')}</th>
                  <th>{t('AccessReviewPage.thDecision')}</th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  <tr key={item.id}>
                    <td>
                      <div style={{ fontWeight: 600 }}>{item.displayName || '—'}</div>
                      <div style={{ fontSize: 12, color: '#64748b' }}>{item.email}</div>
                    </td>
                    <td><SysChip system={item.system} /></td>
                    <td style={{ maxWidth: 200 }}>{item.role}</td>
                    <td><RiskBadge level={item.riskLevel} /></td>
                    <td>
                      {item.decision === 'PENDING' && selected.status === 'OPEN' ? (
                        <div className="ar-decision-btns">
                          <button className="ar-btn-approve"
                                  onClick={() => setDecision(item.id, 'APPROVED')}>{t('AccessReviewPage.btnApprove')}</button>
                          <button className="ar-btn-revoke"
                                  onClick={() => setDecision(item.id, 'REVOKED')}>{t('AccessReviewPage.btnRevoke')}</button>
                          <button className="ar-btn-escalate"
                                  onClick={() => setDecision(item.id, 'ESCALATED')}>{t('AccessReviewPage.btnEscalate')}</button>
                        </div>
                      ) : (
                        <span className={`ar-decided ar-decided-${item.decision?.toLowerCase()}`}>
                          {item.decision === 'APPROVED'  ? t('AccessReviewPage.decidedApproved')  :
                           item.decision === 'REVOKED'   ? t('AccessReviewPage.decidedRevoked')   :
                           item.decision === 'ESCALATED' ? t('AccessReviewPage.decidedEscalated') : t('AccessReviewPage.decidedPending')}
                          {item.decidedBy && <span style={{ color: '#94a3b8', fontWeight: 400 }}>{t('AccessReviewPage.decidedBySuffix', { by: item.decidedBy })}</span>}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
